import { LitElement, html, css } from 'lit';

/**
 * Composant <ui-slider>
 * Curseur de sélection numérique avec tooltip de valeur en direct
 */
export class UISlider extends LitElement {
  static formAssociated = true;

  static properties = {
    name: { type: String },
    value: { type: Number, reflect: true },
    min: { type: Number },
    max: { type: Number },
    step: { type: Number },
    disabled: { type: Boolean, reflect: true },
    required: { type: Boolean, reflect: true },
    label: { type: String, reflect: true }
  };

  static styles = css`
    :host {
      display: inline-block;
      width: 100%;
      position: relative;
      font-family: var(--ui-font-family);
      font-size: var(--ui-font-size-base);
      color: var(--ui-text);
      padding-top: 24px; /* Espace pour le tooltip */
      box-sizing: border-box;
    }

    .ui-label {
      display: block;
      margin-bottom: 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 500;
      color: var(--ui-text-muted);
      text-align: left;
    }

    .slider-container {
      position: relative;
      width: 100%;
      display: flex;
      align-items: center;
      height: 24px;
    }

    /* Le Tooltip */
    .slider-tooltip {
      position: absolute;
      top: -24px;
      transform: translateX(-50%);
      background-color: var(--ui-primary);
      color: var(--ui-text-on-primary);
      padding: 2px 6px;
      font-size: var(--ui-font-size-sm);
      font-weight: 600;
      border-radius: var(--ui-radius-sm);
      pointer-events: none;
      white-space: nowrap;
      box-shadow: var(--ui-shadow-sm);
      transition: left var(--ui-transition-fast) ease-out, opacity 0.15s;
      opacity: 0;
    }

    .slider-container:hover .slider-tooltip,
    .slider-container:focus-within .slider-tooltip {
      opacity: 1;
    }

    /* Track et Thumb du Range Input */
    .slider-input {
      -webkit-appearance: none;
      width: 100%;
      height: 6px;
      border-radius: var(--ui-radius-full);
      background-color: var(--ui-border);
      outline: none;
      margin: 0;
      cursor: pointer;
      transition: background-color var(--ui-transition-fast);
    }

    .slider-input:focus-visible {
      box-shadow: 0 0 0 4px var(--ui-focus-ring);
    }

    .slider-input:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }

    /* Styliser le Thumb (Webkit) */
    .slider-input::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background-color: var(--ui-primary);
      border: 2px solid var(--ui-surface);
      box-shadow: var(--ui-shadow-md);
      transition: transform var(--ui-transition-fast), background-color var(--ui-transition-fast);
    }

    .slider-input::-webkit-slider-thumb:hover {
      transform: scale(1.15);
      background-color: var(--ui-primary-hover);
    }

    /* Styliser le Thumb (Firefox) */
    .slider-input::-moz-range-thumb {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background-color: var(--ui-primary);
      border: 2px solid var(--ui-surface);
      box-shadow: var(--ui-shadow-md);
      transition: transform var(--ui-transition-fast), background-color var(--ui-transition-fast);
      cursor: pointer;
    }

    .slider-input::-moz-range-thumb:hover {
      transform: scale(1.15);
      background-color: var(--ui-primary-hover);
    }
  `;

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.name = '';
    this.value = 50;
    this.min = 0;
    this.max = 100;
    this.step = 1;
    this.disabled = false;
    this.required = false;
    this.label = '';
  }

  get validity() { return this.internals.validity; }
  get validationMessage() { return this.internals.validationMessage; }
  get willValidate() { return this.internals.willValidate; }
  checkValidity() { return this.internals.checkValidity(); }
  reportValidity() { return this.internals.reportValidity(); }

  updated(changedProperties) {
    if (changedProperties.has('value') || changedProperties.has('required')) {
      this.internals.setFormValue(this.value !== undefined ? String(this.value) : '');
      this._updateValidity();
    }
  }

  _updateValidity() {
    const hasValue = this.value !== undefined && this.value !== null && !isNaN(this.value);
    if (this.required && !hasValue) {
      this.internals.setValidity({ valueMissing: true }, 'Veuillez choisir une valeur.', this.shadowRoot.querySelector('.slider-input'));
    } else {
      this.internals.setValidity({});
    }
  }

  _handleInput(e) {
    const val = parseFloat(e.target.value);
    const oldValue = this.value;
    this.value = val;

    if (oldValue !== val) {
      this.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
      this.dispatchEvent(new CustomEvent('ui-change', {
        detail: { value: val },
        bubbles: true,
        composed: true
      }));
    }
  }

  render() {
    // Calcul de la position en % pour positionner le tooltip au dessus du thumb
    const minVal = this.min !== undefined ? this.min : 0;
    const maxVal = this.max !== undefined ? this.max : 100;
    const pct = ((this.value - minVal) / (maxVal - minVal)) * 100;

    return html`
      ${this.label ? html`<label class="ui-label" part="label">${this.label}</label>` : ''}
      <div class="slider-container ui-component">
        <!-- Tooltip volant -->
        <div class="slider-tooltip" style="left: ${pct}%;">
          ${this.value}
        </div>

        <input 
          type="range"
          class="slider-input" part="input"
          .value="${String(this.value)}"
          min="${this.min}"
          max="${this.max}"
          step="${this.step}"
          ?disabled="${this.disabled}"
          @input="${this._handleInput}"
        >
      </div>
    `;
  }
}

if (!customElements.get('ui-slider')) {
  customElements.define('ui-slider', UISlider);
}
